import React, { PropsWithChildren } from 'react';
import { Typography, Alert, Collapse, FormControlLabel, Switch, Select, SelectChangeEvent, MenuItem } from '@mui/material';
import './QuoteExperiencePanel.component.css';
import { formatSchemaTypeTitle } from '../../QuoteExperience.helpers';

export type QEFormEntryMode = 'form' | 'json';
export type QEFormEntryModeResponse = {
    success: boolean;
    error?: string;
}

export interface QuoteExperiencePanelProps {
    saveErrors?: string[];
    formTitle: string;
    defaultMode?: QEFormEntryMode;
    validationMessages?: string[];
    showFormToggle?: boolean;
    showFormSelect?: boolean;
    selectedForm?: string;
    selectFormOptions?: string[];
    onFormSelected?: (selectedForm: string) => void;
    formMode?: QEFormEntryMode;
    onFormModeToggle?: (formMode: QEFormEntryMode) => void;
};

export const QuoteExperiencePanel: React.FC<PropsWithChildren<QuoteExperiencePanelProps>> = ({
    saveErrors,
    formTitle,
    validationMessages,
    selectedForm,
    showFormSelect,
    selectFormOptions,
    showFormToggle,
    formMode,
    onFormSelected,
    onFormModeToggle,
    children
}) => {
    return (
        <>
            {/* Save Error Alert */}
            <Collapse in={(saveErrors || []).length > 0}>
                {
                    (saveErrors || []).filter(error => !!error).map((saveError, idx) => (
                        <Alert key={idx} severity="error" sx={{ marginBottom: 2 }}>
                            <Typography variant="body2" component="div">
                                <strong>Save Error:</strong> {saveError}
                            </Typography>
                        </Alert>
                    ))
                }
            </Collapse>

            {/* Validation Error Alert */}
            <Collapse in={(validationMessages || []).length > 0}>
                {(validationMessages || []).length > 0 && (
                    <Alert severity="warning" sx={{ marginBottom: 2 }}>
                        <Typography variant="body2" component="div">
                            <strong>Validation Issues:</strong>
                        </Typography>
                        <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 20 }}>
                            {validationMessages!.map((msg, idx) => (
                                <li key={idx}>
                                    <Typography variant="body2">{msg}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}
            </Collapse>

            <div className='app-qe-panel' >
                <div className='app-qe-panel-header' >
                    <div className='app-qe-panel-header-control' >
                        {
                            showFormToggle && onFormModeToggle ? (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formMode === 'json'}
                                            onChange={(e) => {
                                                onFormModeToggle(e.target.checked ? 'json' : 'form')
                                            }}
                                            inputProps={{ 'aria-label': 'Toggle form / JSON' }}
                                        />
                                    }
                                    label={formMode === 'json' ? 'Raw JSON' : 'Form'}
                                />
                            ) : null
                        }
                    </div>
                    <div className='app-qe-panel-title' >{formTitle}</div>
                    <div className='app-qe-panel-header-control app-qe-panel-select' >
                        {
                            showFormSelect && onFormSelected && selectFormOptions ? (
                                <Select
                                    id="status"
                                    name="status"
                                    value={selectedForm}
                                    onChange={(event: SelectChangeEvent<string>) => onFormSelected(event.target.value)}
                                >
                                    {
                                        selectFormOptions.map(option => (
                                            <MenuItem key={option} value={option} >{formatSchemaTypeTitle(option)}</MenuItem>
                                        ))
                                    }
                                </Select>
                            ) : null
                        }
                    </div>
                </div>
                <div className='app-qe-panel-body' >
                    {children}
                </div>
            </div>
        </>
    );
};
